"use client";

import { IdentityProvider, useCurrentAccountId } from "@zapier/identity";
import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import { type ReactNode, useEffect, useState } from "react";

import { isDifferentAccount } from "@/lib/utils/account";

import { AccountMismatchBanner } from "./account-mismatch-banner";

/**
 * Detects account mismatches between the IdentityProvider cookie and the
 * NextAuth session, and shows a banner when they differ. Server-side
 * reconciliation in the JWT callback handles the actual session sync.
 */
const CurrentAccountIdRedirect = () => {
  const currentAccountId = useCurrentAccountId();
  const pathname = usePathname();

  const { data: session, status } = useSession();
  const sessionAccountId = session?.user.currentAccountId;

  const [showMismatchBanner, setShowMismatchBanner] = useState(false);

  useEffect(() => {
    if (status === "loading") return;

    if (session?.status === "invalid") return;

    // Wait for IdentityProvider to read the cookie. Without this,
    // isDifferentAccount(undefined, sessionAccountId) returns false and
    // the "accounts match" branch fires prematurely — triggering the
    // post-switch redirect before the session has actually reconciled.
    if (currentAccountId == null) return;

    if (
      pathname.startsWith("/embed") ||
      pathname.startsWith("/embed/login-popup") ||
      pathname.startsWith("/embed/login-success") ||
      pathname.startsWith("/share") ||
      pathname.startsWith("/switching-account")
    ) {
      return;
    }

    // Check if there's a mismatch and show popup to notify user
    if (isDifferentAccount(currentAccountId, sessionAccountId)) {
      // a user-initiated account switch
      const switchingTarget = sessionStorage.getItem(
        "account-switching-target",
      );

      if (switchingTarget) {
        // don't show popup, but also don't clear the flag yet - let the redirect happen
        return;
      }

      console.log("Account mismatch detected, showing popup", {
        currentAccountId,
        sessionAccountId,
      });
      setShowMismatchBanner(true);
    } else {
      // clear state if accounts match
      const wasSwitching = sessionStorage.getItem("account-switching-target");
      sessionStorage.removeItem("account-switching-target");

      // After a user-initiated account switch, redirect to the stored target
      // (e.g. back to the OAuth consent page). The Django redirect chain
      // mangles nested URL encoding, so we stash the real target in sessionStorage.
      const postSwitchRedirect = sessionStorage.getItem("post-switch-redirect");
      sessionStorage.removeItem("post-switch-redirect");
      if (
        wasSwitching &&
        postSwitchRedirect &&
        String(sessionAccountId) === wasSwitching
      ) {
        // Only allow same-origin (relative) redirects
        if (postSwitchRedirect.startsWith("/")) {
          window.location.replace(postSwitchRedirect);
          return;
        }
      }

      setShowMismatchBanner(false);
    }
  }, [currentAccountId, sessionAccountId, status, session?.status, pathname]);

  return <>{showMismatchBanner && <AccountMismatchBanner />}</>;
};

export const CustomCurrentAccountId = ({
  children,
}: {
  children: ReactNode;
}) => {
  const pathname = usePathname();

  // Skip IdentityProvider for embed routes to avoid 404 API calls
  if (pathname.startsWith("/embed")) {
    return <>{children}</>;
  }

  // We still render IdentityProvider to maintain consistent hook usage and
  // avoid React errors, but server-side reconciliation in the JWT callback
  // handles account syncing. CurrentAccountIdRedirect only shows a banner
  // when a mismatch is detected.
  return (
    <IdentityProvider>
      <CurrentAccountIdRedirect />
      {children}
    </IdentityProvider>
  );
};
